import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Language } from '@/types';
import { Menu, X, ArrowRight, Sprout } from 'lucide-react';

interface CropLensNavbarProps {
  lang: Language;
}

const CropLensNavbar: React.FC<CropLensNavbarProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: lang === 'en' ? 'Product' : 'المنتج', href: '/products/croplens' },
    { label: lang === 'en' ? 'Features' : 'الميزات', href: '/products/croplens#engine-details' },
    { label: lang === 'en' ? 'Workspace' : 'مساحة العمل', href: '/products/croplens/workspace' },
    { label: lang === 'en' ? 'APIs & Docs' : 'الوثائق البرمجية', href: '/docs' },
    { label: lang === 'en' ? 'VEXL Corporate' : 'منصة فكسل الرئيسية', href: '/' }
  ];

  // Close menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-4 left-4 right-4 md:left-10 md:right-10 z-[100] flex justify-between items-center px-6 md:px-10 h-16 liquid-glass shadow-[0_12px_40px_rgba(0,0,0,0.4)] rounded-2xl"
      >
        {/* BRAND / LOGO */}
        <div className="flex items-center gap-2 transition-all duration-300">
          <Link to="/products/croplens" className="flex items-center gap-2.5 group">
            <div className="w-1.5 h-6 bg-emerald-500 rounded-sm group-hover:shadow-[0_0_15px_rgba(16,185,129,0.8)] transition-all" />
            <span className="text-lg font-black tracking-tight text-white uppercase italic flex items-center gap-1.5">
              <span>VEXL CropLens</span>
              {location.pathname.includes('/workspace') && (
                <span className="text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)] font-black italic text-sm">WORKSPACE</span>
              )}
            </span>
          </Link>
        </div>

        {/* DESKTOP LINKS */}
        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map((link, i) => {
            const isExternal = link.href.startsWith('http');
            const isActive = location.pathname === link.href;
            const className = `text-[11px] font-bold uppercase tracking-widest transition-all ${
              isActive ? 'text-emerald-400 font-extrabold' : 'text-slate-400 hover:text-emerald-400'
            }`;
            
            return isExternal ? (
              <a key={i} href={link.href} className={className}>
                {link.label}
              </a>
            ) : (
              <Link key={i} to={link.href} className={className}>
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* ACTIONS / MOBILE TOGGLE */}
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/products/croplens/workspace"
            className="hidden sm:flex items-center gap-1.5 px-5 py-2 rounded-xl liquid-glass-button-primary text-white text-[11px] font-black uppercase tracking-widest"
          >
            <Sprout size={13} />
            <span>{lang === 'en' ? 'Launch Workspace' : 'أطلق مساحة العمل'}</span>
          </Link>

          {/* Custom Morphing Hamburger Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:bg-white/5 rounded-lg transition-colors relative z-[110]"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center gap-1.5 overflow-hidden">
              <motion.span
                animate={{
                  rotate: isMobileMenuOpen ? 45 : 0,
                  y: isMobileMenuOpen ? 8 : 0,
                }}
                className="block w-6 h-0.5 bg-current rounded-full origin-center"
              />
              <motion.span
                animate={{
                  opacity: isMobileMenuOpen ? 0 : 1,
                  x: isMobileMenuOpen ? 20 : 0
                }}
                className="block w-6 h-0.5 bg-current rounded-full"
              />
              <motion.span
                animate={{
                  rotate: isMobileMenuOpen ? -45 : 0,
                  y: isMobileMenuOpen ? -8 : 0,
                }}
                className="block w-6 h-0.5 bg-current rounded-full origin-center"
              />
            </div>
          </button>
        </div>
      </motion.nav>

      {/* MOBILE MENU OVERLAY */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[90] bg-[#02040a] pt-28 pb-12 px-8 lg:hidden overflow-y-auto"
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 to-transparent pointer-events-none" />

            <div className="flex flex-col gap-8 relative z-10">
              {navLinks.map((link, i) => {
                const isExternal = link.href.startsWith('http');
                const className = "text-3xl font-bold text-white hover:text-emerald-400 transition-all flex items-center justify-between group";
                
                return isExternal ? (
                  <a key={i} href={link.href} className={className}>
                    <span>{link.label}</span>
                    <ArrowRight className="opacity-0 group-hover:opacity-100 transition-all -rotate-45" size={24} />
                  </a>
                ) : (
                  <Link key={i} to={link.href} className={className}>
                    <span>{link.label}</span>
                    <ArrowRight className="opacity-0 group-hover:opacity-100 transition-all -rotate-45" size={24} />
                  </Link>
                );
              })}

              <div className="h-px bg-white/10 my-4" />

              <Link
                to="/products/croplens/workspace"
                className="w-full py-5 rounded-2xl bg-emerald-500 text-white text-center text-lg font-black uppercase tracking-widest shadow-lg flex items-center justify-center gap-2"
              >
                <Sprout size={18} />
                <span>{lang === 'en' ? 'Launch Workspace' : 'أطلق مساحة العمل'}</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CropLensNavbar;
